import { supabase } from "@/integrations/supabase/client";

export type PendingAction = {
  id: string;
  title: string;
  status: "draft" | "under_review" | "pending_approval" | "approved";
  expires_in_days?: number; // only for approved policies nearing renewal
  // internal sorting aid
  priority?: number;
  // dynamic metadata
  created_at?: string;
  assigned_reviewers?: string[];
  assigned_approvers?: string[];
  next_renewal_date?: string | null;
  subtitle?: string;
};

export type UnresolvedComment = {
  id: string;
  policy_id: string;
  policy_title: string;
  content: string;
  created_at: string;
};

export async function fetchPendingActions(
  userId: string,
  companyId: string,
  roles: string[]
): Promise<PendingAction[]> {
  // viewers have no personal actions
  if (roles.includes("viewer") && roles.length === 1) return [];

  const results: PendingAction[] = [];

  // Creator/Admin: drafts created by the user
  if (roles.includes("creator") || roles.includes("admin")) {
    const { data } = await supabase
      .from("policies")
      .select("id, title, status, created_at, assigned_reviewers, assigned_approvers")
      .eq("company_id", companyId)
      .eq("created_by", userId)
      .eq("status", "draft");
    (data || []).forEach((p: any) => results.push({ 
      id: p.id, 
      title: p.title, 
      status: p.status, 
      priority: 200,
      created_at: p.created_at,
      assigned_reviewers: p.assigned_reviewers,
      assigned_approvers: p.assigned_approvers
    }));
  }

  // Reviewer/Admin: under review assigned to the user
  if (roles.includes("reviewer") || roles.includes("admin")) {
    const { data } = await supabase
      .from("policy_workflow")
      .select("policy_id, policies(title,status,created_at,assigned_reviewers,assigned_approvers)")
      .eq("company_id", companyId)
      .eq("assigned_to", userId)
      .eq("status", "under_review");
    (data || []).forEach((row: any) => {
      // Only include if policy status is actually under_review (not approved or other statuses)
      if (row.policies && row.policies.status === "under_review") {
        results.push({ 
          id: row.policy_id, 
          title: row.policies.title, 
          status: row.policies.status, 
          priority: 150,
          created_at: row.policies.created_at,
          assigned_reviewers: row.policies.assigned_reviewers,
          assigned_approvers: row.policies.assigned_approvers
        });
      }
    });
  }

  // Approver/Admin: pending approval assigned to the user
  if (roles.includes("approver") || roles.includes("admin")) {
    const { data } = await supabase
      .from("policy_workflow")
      .select("policy_id, policies(title,status,created_at,assigned_reviewers,assigned_approvers)")
      .eq("company_id", companyId)
      .eq("assigned_to", userId)
      .eq("status", "pending_approval");
    (data || []).forEach((row: any) => {
      // Only include if policy status is actually pending_approval (not approved or other statuses)
      if (row.policies && row.policies.status === "pending_approval") {
        results.push({ 
          id: row.policy_id, 
          title: row.policies.title, 
          status: row.policies.status, 
          priority: 100,
          created_at: row.policies.created_at,
          assigned_reviewers: row.policies.assigned_reviewers,
          assigned_approvers: row.policies.assigned_approvers
        });
      }
    });
  }

  // Approved but expiring/renewals soon: show to creator, reviewer (stakeholder), and approver
  // Threshold: next 30 days (or overdue)
  const threshold = new Date();
  threshold.setDate(threshold.getDate() + 30);

  // Fetch approved with upcoming renewals
  const { data: approved } = await supabase
    .from("policies")
    .select("id, title, status, next_renewal_date, created_by, created_at, assigned_reviewers, assigned_approvers")
    .eq("company_id", companyId)
    .eq("status", "approved")
    .not("next_renewal_date", "is", null)
    .lte("next_renewal_date", threshold.toISOString());

  if (approved && approved.length > 0) {
    // Fetch stakeholder policy ids for this user (reviewer or approver historical assignments)
    const { data: stakeholderWF } = await supabase
      .from("policy_workflow")
      .select("policy_id, status")
      .eq("company_id", companyId)
      .eq("assigned_to", userId)
      .in("status", ["under_review", "pending_approval", "approved"]);

    const stakeholderIds = new Set<string>((stakeholderWF || []).map((w: any) => w.policy_id));

    (approved || []).forEach((p: any) => {
      const rn = p.next_renewal_date ? Math.ceil((new Date(p.next_renewal_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : undefined;
      const isCreatorStakeholder = p.created_by === userId && (roles.includes("creator") || roles.includes("admin"));
      const isWorkflowStakeholder = stakeholderIds.has(p.id) && (roles.includes("reviewer") || roles.includes("approver") || roles.includes("admin"));
      // Only include if renewal date is set AND user is a stakeholder
      if ((isCreatorStakeholder || isWorkflowStakeholder) && typeof rn === 'number') {
        // Highest priority block (0..30 or negative for overdue)
        const subtitle = rn < 0
          ? `Expired ${Math.abs(rn)} days ago`
          : `About to expire in ${rn} days`;
        results.push({ 
          id: p.id, 
          title: p.title, 
          status: "approved", 
          expires_in_days: rn, 
          priority: rn,
          created_at: p.created_at,
          assigned_reviewers: p.assigned_reviewers,
          assigned_approvers: p.assigned_approvers,
          next_renewal_date: p.next_renewal_date,
          subtitle
        });
      }
    });
  }

  // De-duplicate by policy id, preferring entries with expiry info for approved policies
  const seen = new Map<string, PendingAction>();
  results.forEach((r) => {
    const existing = seen.get(r.id);
    if (!existing) {
      seen.set(r.id, r);
    } else if (r.status === "approved" && r.expires_in_days !== undefined && existing.expires_in_days === undefined) {
      // Replace existing with one that has expiry info
      seen.set(r.id, r);
    } else if (existing.status === "approved" && existing.expires_in_days === undefined && r.expires_in_days !== undefined) {
      // Keep the one with expiry info
      seen.set(r.id, r);
    }
    // Otherwise keep existing (first occurrence)
  });
  const deduped = Array.from(seen.values());

  // Deterministic prioritization: lower priority value first
  deduped.sort((a, b) => {
    const pa = typeof a.priority === 'number' ? a.priority! : 9999;
    const pb = typeof b.priority === 'number' ? b.priority! : 9999;
    if (pa !== pb) return pa - pb;
    // tie-breaker for expiring: closest first
    const aExp = typeof a.expires_in_days === 'number';
    const bExp = typeof b.expires_in_days === 'number';
    if (aExp && bExp) return (a.expires_in_days as number) - (b.expires_in_days as number);
    return 0;
  });

  return deduped;
}

export async function fetchUnresolvedComments(
  userId: string,
  companyId: string,
  roles: string[]
): Promise<UnresolvedComment[]> {
  // Collect policy IDs where user is a stakeholder
  const stakeholderPolicyIds = new Set<string>();

  // creator/admin – creator's policies
  if (roles.includes("creator") || roles.includes("admin")) {
    const { data } = await supabase
      .from("policies")
      .select("id")
      .eq("company_id", companyId)
      .eq("created_by", userId);
    (data || []).forEach((p: any) => stakeholderPolicyIds.add(p.id));
  }

  // reviewer/admin – assigned to user
  if (roles.includes("reviewer") || roles.includes("admin")) {
    const { data } = await supabase
      .from("policy_workflow")
      .select("policy_id")
      .eq("company_id", companyId)
      .eq("assigned_to", userId)
      .in("status", ["under_review"]);
    (data || []).forEach((w: any) => stakeholderPolicyIds.add(w.policy_id));
  }

  // approver/admin – assigned to user
  if (roles.includes("approver") || roles.includes("admin")) {
    const { data } = await supabase
      .from("policy_workflow")
      .select("policy_id")
      .eq("company_id", companyId)
      .eq("assigned_to", userId)
      .in("status", ["pending_approval"]);
    (data || []).forEach((w: any) => stakeholderPolicyIds.add(w.policy_id));
  }

  if (stakeholderPolicyIds.size === 0) return [];

  const { data } = await supabase
    .from("policy_comments")
    .select("id, policy_id, content, created_at, policies(title)")
    .eq("company_id", companyId)
    .eq("is_resolved", false)
    .in("policy_id", Array.from(stakeholderPolicyIds));

  return (data || []).map((row: any) => ({
    id: row.id,
    policy_id: row.policy_id,
    policy_title: row.policies?.title || "Untitled Policy",
    content: row.content,
    created_at: row.created_at,
  }));
}


